import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Avatar,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Fade,
  Zoom,
  Snackbar
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import userPhotoService from '../user-preferences/photos/UserPhotoService';
import userPreferencesService from '../user-preferences/services/userPreferencesService';

const UserPhotoUpload = ({ userId, currentPhoto, onPhotoChange, size = 120 }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  
  const [isUploading, setIsUploading] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [showEditMode, setShowEditMode] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Função para obter inicial do usuário de forma segura
  const getUserInitial = useCallback(() => {
    if (!userId) return 'U';
    
    if (typeof userId === 'string' && userId.length > 0) {
      return userId.charAt(0).toUpperCase();
    }
    if (typeof userId === 'number') {
      return userId.toString().charAt(0);
    }
    return 'U';
  }, [userId]);

  // Função para mostrar notificação de sucesso
  const showSuccessNotification = useCallback((message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
  }, []);

  // Função para mostrar notificação de erro
  const showErrorNotification = useCallback((message) => {
    setError(message);
  }, []);

  // Função para lidar com o upload de arquivo
  const handleFileUpload = useCallback(async (file) => {
    if (!userId) {
      showErrorNotification('Usuário não identificado');
      return;
    }

    try {
      setError(null);
      setIsUploading(true);

      // Validações do arquivo
      if (!file) {
        throw new Error('Nenhum arquivo selecionado');
      }

      // Verificar tipo de arquivo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Formato de arquivo não suportado. Use JPG, PNG ou WebP');
      }

      // Verificar tamanho (máximo 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('Arquivo muito grande. Tamanho máximo: 5MB');
      }

      // Comprimir imagem para reduzir payload (evitar 413 na API)
      const compressedBlob = await userPhotoService.compressImage(
        file,
        800, // maxWidth
        800, // maxHeight
        0.7  // quality
      );

      // Converter comprimida para base64
      const compressedBase64 = await userPhotoService.fileToBase64(compressedBlob);

      // Preview com imagem comprimida
      setPreviewPhoto(compressedBase64);

      // Metadados da foto
      const metadata = {
        fileName: file.name,
        originalSize: file.size,
        compressedSize: compressedBlob.size,
        uploadDate: new Date().toISOString(),
        mimeType: 'image/jpeg'
      };

      // ✅ Salvar na API primeiro (com fallback para localStorage)
      try {
        const saved = await userPreferencesService.saveUserPhoto(userId, compressedBase64, metadata);
        
        if (saved) {
          // Criar objeto de dados da foto para o componente pai
          const photoData = {
            userId,
            photoUrl: compressedBase64,
            ...metadata
          };

          // Notificar componente pai
          if (onPhotoChange) onPhotoChange(photoData);

          showSuccessNotification('Foto atualizada com sucesso!');
          setShowEditMode(false);
        } else {
          throw new Error('Não foi possível salvar a foto');
        }
      } catch (apiError) {
        console.error('Erro ao salvar foto na API:', apiError);
        // Fallback: salvar apenas localmente
        const photoData = {
          userId,
          photoUrl: compressedBase64,
          ...metadata
        };
        localStorage.setItem(`user_photo_${userId}`, JSON.stringify(photoData));
        if (onPhotoChange) onPhotoChange(photoData);
        showSuccessNotification('Foto salva localmente (API indisponível)');
        setShowEditMode(false);
      }

    } catch (error) {
      showErrorNotification(error.message);
    } finally {
      setIsUploading(false);
    }
  }, [userId, onPhotoChange, showSuccessNotification, showErrorNotification]);

  // Função para remover foto
  const handleRemovePhoto = useCallback(async () => {
    if (!userId) {
      showErrorNotification('Usuário não identificado');
      return;
    }

    try {
      setIsUploading(true);
      
      // ✅ Remover da API primeiro (com fallback para localStorage)
      try {
        const removed = await userPreferencesService.removeUserPhoto(userId);
        if (!removed) {
          // Se falhou na API, remover do localStorage mesmo assim
          localStorage.removeItem(`user_photo_${userId}`);
        }
      } catch (apiError) {
        console.error('Erro ao remover foto da API:', apiError);
        // Fallback: remover apenas do localStorage
        localStorage.removeItem(`user_photo_${userId}`);
      }
      
      // Limpar preview
      setPreviewPhoto(null);
      
      // Notificar componente pai
      if (onPhotoChange) {
        onPhotoChange(null);
      }

      showSuccessNotification('Foto removida com sucesso!');
      setShowEditMode(false);

    } catch (error) {
      showErrorNotification('Erro ao remover foto');
    } finally {
      setIsUploading(false);
    }
  }, [userId, onPhotoChange, showSuccessNotification, showErrorNotification]);

  // Função para cancelar edição
  const handleCancelEdit = useCallback(() => {
    setPreviewPhoto(null);
    setError(null);
    setShowEditMode(false);
  }, []);

  // Função para abrir seletor de arquivo
  const handleOpenFileSelector = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Função para lidar com mudança de arquivo
  const handleFileChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Limpar input para permitir seleção do mesmo arquivo
    event.target.value = '';
  }, [handleFileUpload]);

  // Função para drag and drop
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.currentTarget.style.borderColor = theme.palette.primary.main;
    event.currentTarget.style.backgroundColor = alpha(theme.palette.primary.main, 0.05);
  }, [theme.palette.primary.main]);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.currentTarget.style.borderColor = theme.palette.divider;
    event.currentTarget.style.backgroundColor = 'transparent';
  }, [theme.palette.divider]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.currentTarget.style.borderColor = theme.palette.divider;
    event.currentTarget.style.backgroundColor = 'transparent';
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload, theme.palette.divider]);

  // Determinar foto atual
  const currentPhotoUrl = previewPhoto || currentPhoto;

  // Validar se userId está disponível - APÓS todos os hooks
  if (!userId) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Usuário não identificado. Faça login para continuar.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center' }}>
      {/* Título da seção */}
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2, 
          fontWeight: 500,
          color: 'text.primary'
        }}
      >
        📸 Foto do Perfil
      </Typography>

      {/* Container da foto */}
      <Box
        sx={{
          position: 'relative',
          display: 'inline-block',
          mb: 2
        }}
      >
        {/* Avatar principal */}
        <Avatar
          src={currentPhotoUrl}
          sx={{
            width: size,
            height: size,
            fontSize: size * 0.4,
            border: `3px solid ${theme.palette.primary.main}`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
            cursor: showEditMode ? 'pointer' : 'default',
            transition: 'all 0.3s ease',
            '&:hover': showEditMode ? {
              transform: 'scale(1.05)',
              boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`
            } : {}
          }}
          onClick={showEditMode ? handleOpenFileSelector : undefined}
        >
          {!currentPhotoUrl && getUserInitial()}
        </Avatar>

        {/* Botão de edição (hover) */}
        {!showEditMode && (
          <Fade in={!showEditMode} timeout={300}>
            <IconButton
              onClick={() => setShowEditMode(true)}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                border: `3px solid ${theme.palette.background.paper}`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Fade>
        )}

        {/* Loading overlay */}
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              borderRadius: '50%'
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
      </Box>

      {/* Modo de edição */}
      {showEditMode && (
        <Zoom in={showEditMode} timeout={300}>
          <Box sx={{ mt: 2 }}>
            {/* Área de drag and drop */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: `2px dashed ${theme.palette.primary.main}`,
                borderRadius: 2,
                p: 3,
                mb: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
              onClick={handleOpenFileSelector}
            >
              <PhotoCameraIcon 
                sx={{ 
                  fontSize: 48, 
                  color: 'primary.main',
                  mb: 1 
                }} 
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Clique ou arraste uma foto aqui
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JPG, PNG ou WebP • Máximo 5MB
              </Typography>
            </Box>

            {/* Botões de ação */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleRemovePhoto}
                disabled={isUploading || !currentPhotoUrl}
                sx={{ minWidth: '100px' }}
              >
                Remover
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={handleCancelEdit}
                disabled={isUploading}
                sx={{ minWidth: '100px' }}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
        </Zoom>
      )}

      {/* Input de arquivo oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Mensagem de erro */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2, maxWidth: '400px', mx: 'auto' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Informações da foto */}
      {currentPhotoUrl && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            ✅ Foto carregada com sucesso
          </Typography>
        </Box>
      )}

      {/* Dicas de uso */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
        <Typography variant="body2" color="info.main" sx={{ fontWeight: 500, mb: 1 }}>
          💡 Dicas para uma boa foto:
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          • Use uma foto quadrada para melhor resultado
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          • Certifique-se de que seu rosto esteja bem iluminado
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          • Evite fotos muito escuras ou desfocadas
        </Typography>
      </Box>

      {/* Snackbar de sucesso */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserPhotoUpload;
